import { api } from "./api";

export const magicStyles     = () => api.get("/magic/styles").then((r) => r.data);
export const magicChallenge  = () => api.get("/magic/challenge").then((r) => r.data);
export const magicStatus     = () => api.get("/magic/status").then((r) => r.data);
export const magicGenerate   = (payload) => api.post("/magic/generate", payload, {
  // Backend now offloads storage upload + model to a background task, so
  // this POST returns in <1s. Still give it 90s headroom in case a huge
  // photo makes the pre-decode step slow on low-end phones.
  timeout: 90000,
}).then((r) => r.data);
export const magicJob        = (jobId) => api.get(`/magic/jobs/${jobId}`, {
  timeout: 15000,
}).then((r) => r.data);

/** Poll a generation job every {interval}ms until it's completed or failed.
 * Calls onTick each poll so the UI can show elapsed seconds. */
export async function pollMagicJob(jobId, { interval = 2000, timeoutMs = 180000, onTick } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const r = await magicJob(jobId);
    onTick?.(r, Math.round((Date.now() - started) / 1000));
    if (r.status === "completed") return r;
    if (r.status === "failed") throw new Error(r.error || "Generation failed");
    await new Promise((res) => setTimeout(res, interval));
  }
  throw new Error("Generation timed out. Please try again.");
}
export const magicGallery    = (limit = 60) => api.get("/magic/gallery", { params: { limit } }).then((r) => r.data);
export const magicGeneration = (gid) => api.get(`/magic/generation/${gid}`).then((r) => r.data);
export const magicToggleShare = (gid, is_public) => api.patch(`/magic/generation/${gid}`, { is_public }).then((r) => r.data);
export const magicDelete     = (gid) => api.delete(`/magic/generation/${gid}`).then((r) => r.data);

// --- Feed + boost ---------------------------------------------------------
export const magicFeed        = (params = {}) => api.get("/magic/feed", { params }).then((r) => r.data);
export const magicLike        = (gid) => api.post(`/magic/generation/${gid}/like`).then((r) => r.data);
export const magicBoost       = () => api.post("/magic/boost").then((r) => r.data);
export const magicBoostStatus = () => api.get("/magic/boost/status").then((r) => r.data);
export const magicWeeklyWinner = () => api.get("/magic/weekly-winner").then((r) => r.data);

/** Resolve a storage path to a URL the browser can render. If token is
 * supplied, we append it as ?auth= so private images render inside <img>.
 */
export function magicFileUrl(path, token) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const q = token ? `?auth=${encodeURIComponent(token)}` : "";
  return `${base}/api/magic/files/${path}${q}`;
}

/** Read a File/Blob and return { base64 (no data-URL prefix), dataUrl, mime } */
export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const [head, body] = dataUrl.split(",", 2);
      const mimeMatch = head.match(/data:([^;]+)/);
      resolve({ base64: body || "", dataUrl, mime: mimeMatch ? mimeMatch[1] : "image/png" });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Downscale an image File to fit within `maxSize` on its longest edge and
 * re-encode as JPEG. Fixes:
 *  1. Gemini "Unable to process input image" 400s on huge phone photos
 *     (12MP raws exceed the model's pixel + payload limit).
 *  2. Slow mobile uploads.
 *  3. EXIF orientation — a fresh canvas render normalises rotation on
 *     every mainstream browser.
 *
 * Returns { base64, dataUrl, mime, width, height, sizeKb }.
 */
export async function readAndDownscaleImage(file, { maxSize = 1536, quality = 0.88 } = {}) {
  if (!file) throw new Error("No file");
  // Load into an ImageBitmap so we get real pixel dimensions AND EXIF rotation.
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Fallback for older browsers — use Image element
    const url = URL.createObjectURL(file);
    try {
      bitmap = await new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = url;
      });
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    }
  }
  const srcW = bitmap.width || bitmap.naturalWidth;
  const srcH = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, maxSize / Math.max(srcW, srcH));
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);

  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  const base64 = dataUrl.split(",", 2)[1] || "";
  const sizeKb = Math.round((base64.length * 3) / 4 / 1024);
  try { bitmap.close?.(); } catch {}
  return { base64, dataUrl, mime: "image/jpeg", width: w, height: h, sizeKb };
}
